import { useCallback, useEffect, useState } from 'react'

/**
 * Every table's dragged widths, keyed by the table.
 *
 * A module map rather than component state: the table unmounts the moment the
 * operator opens another area, and a width that did not survive that would be
 * dragged again on every return. It goes no further than the loaded page —
 * a reload starts from the declared widths.
 */
const WIDTHS = new Map<string, Record<string, number>>()

/**
 * The widths the operator has dragged the columns to, keyed by column.
 *
 * Held outside the columns themselves: a screen rebuilds its column
 * definitions on every render, and a width the reader chose has to outlive
 * that.
 *
 * `enabled` off returns no widths at all, so every cell reads its declared
 * class and the head renders no grip.
 */
export function useColumnWidths(enabled: boolean, storeKey?: string) {
  const [widths, setWidths] = useState<Record<string, number>>(
    () => (storeKey ? WIDTHS.get(storeKey) : undefined) ?? {},
  )

  // Gated on `enabled` as well as on the key: a table that offers no grip has
  // nothing to remember, and writing its empty set would clear what the key
  // holds for a table that does.
  useEffect(() => {
    if (enabled && storeKey) WIDTHS.set(storeKey, widths)
  }, [enabled, storeKey, widths])

  const resize = useCallback((key: string, width: number | null) => {
    setWidths(previous => {
      if (width == null) {
        if (!(key in previous)) return previous
        const { [key]: _dropped, ...rest } = previous
        return rest
      }
      return previous[key] === width ? previous : { ...previous, [key]: width }
    })
  }, [])

  return {
    columnWidths: enabled ? widths : undefined,
    onResizeColumn: enabled ? resize : undefined,
  }
}
